import React, { useEffect, useRef, useState } from "react";
import { Check, MoveLeft, Plus, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useAppState } from "contexts/UserContext";
import { useAuth } from "contexts/AuthContext";
import styles from "./Theme.module.css";
import Header from "components/ui/header/Header"

// experience-backend has no user-preference endpoint yet; honest no-op.
const updateUserCollectionIdByUid = async (
  _userId: string,
  _collectionId: string
) => {
  console.warn(
    "ThemeSettings: updateUserCollectionIdByUid is not supported by experience-backend yet."
  );
};

type ThemeItem = {
  id: string;
  name: string;
};

type CollectionList = {
  id: string;
  name: string;
};

export const ThemeSettings = () => {
  const { themes, themeId, collectionId, setCollectionId, collections } =
    useAppState();
  const { userId } = useAuth();
  const navigate = useNavigate();

  const [selectedId, setSelectedId] = useState<string | null>(
    collectionId ?? null
  );

  const [savingId, setSavingId] = useState<string | null>(null);
  const requestSeq = useRef(0);

  useEffect(() => {
    setSelectedId(themeId ?? null);
  }, [themeId]);

  useEffect(() => {
    if (!collectionId) {
      setCollectionId("rainforest");
    }
  }, []);

  const themeList: ThemeItem[] = themes ?? [];
  const collectionList: CollectionList[] = collections ?? [];

  const saveCollection = async (nextId: string) => {
    if (!userId) return;
    if (savingId) return;
    if (nextId === themeId) return;

    const prevId = themeId ?? null;
    const seq = ++requestSeq.current;

    setSelectedId(nextId);
    setCollectionId(nextId);
    setSavingId(nextId);

    try {
      await updateUserCollectionIdByUid(userId, nextId);

      if (seq !== requestSeq.current) return;
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setSelectedId(prevId);
      setCollectionId(prevId);
      console.error("Failed to update theme", err);
    } finally {
      if (seq === requestSeq.current) setSavingId(null);
    }
  };

  return (
    <div className="root">
      <Header title={"Theme"} />

      <div className="select-component__duration-section" role="list">
        {themeList
          .filter((theme) => theme.id === themeId)
          .map((theme) => {
            return (
              <div
                key={theme.id}
                role="listitem"
                className={styles["list-item"]}
              >
                <button
                  type="button"
                  className={`strength-button select-component__duration-button two-part-button ${styles["theme-button"]}`}
                  onClick={() => saveCollection(theme.id)}
                  aria-pressed
                >
                  <span className={styles["theme-label"]}>{theme.name}</span>
                </button>

                <ChevronRight
                  className={`w-4 h-4 text-red-500 ${styles["list-icon"]}`}
                />
              </div>
            );
          })}
      </div>

      <div className="select-component__duration-section" role="list">
        {collectionList.map((collection) => {
          const isSelected = collection.id === selectedId;
          const isSavingThis = savingId === collection.id;
          const isDisabled = savingId !== null;


          return (
            <div
              key={collection.id}
              role="listitem"
              className={styles["collection-item"]}
            >
              <button
                type="button"
                className={`strength-button select-component__duration-button two-part-button ${styles["collection-button"]}`}
                onClick={() => saveCollection(collection.id)}
                disabled={isDisabled}
                aria-pressed={isSelected}
                aria-busy={isSavingThis}
              >
                <span className={styles["collection-name"]}>
                  {collection.name} {userId}
                </span>
              </button>

              {(isSelected || collection.id === collectionId) &&
                !isSavingThis && (
                  <Check
                    className={`w-4 h-4 text-red-500 ${styles["list-icon"]}`}
                  />
                )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
